import BaseModel from './BaseModel';
import Metro from './Metro';
import MetroId from './MetroId';
import GeoCoordinates from './GeoCoordinates';
import Label from './Label';
import CustomLabel from './CustomLabel';
import Point from './Point';
import MetalPlan from './MetalPlan';
import NetworkEdgeCategory from './NetworkEdgeCategory';
import MetroCode from './MetroCode';
import ServiceConnection from './ServiceConnection';
import Location from './Location';
import Group from './Group';
import getPath from 'utils/getPath';
import InfraComponent from './InfraComponent';
import DeploymentOffset from './DeploymentOffset';
import Capacity from './Capacity';

class MetroConnection extends BaseModel {
  // Fields defined at the bottom to avoid circular dependency
}
export default class ProjectMetro extends BaseModel {
  static fields = [
    { key: 'id', type: MetroId, stringify: true },
    { key: 'name', type: 'string', stringify: true },
    { key: 'region', type: 'string', stringify: true },
    { key: 'code', type: MetroCode, stringify: true },
    { key: 'status', type: 'string', stringify: true },
    { key: 'notes', type: 'string', optional: true },
    { key: 'existing', type: 'boolean' },
    { key: 'serviceOffset', type: Point, stringify: true },
    { key: 'coordinates', type: GeoCoordinates, stringify: true },
    { key: 'labels', type: 'array', of: Label },
    { key: 'customLabels', type: 'array', of: CustomLabel },
    { key: 'services', type: 'array', of: ServiceConnection },
    { key: 'locations', type: 'array', of: Location },
    { key: 'groups', type: 'array', of: Group },

    /* Not yet implemented */
    { key: 'connectedMetros', type: 'array', of: MetroConnection },
    { key: 'metalEnabled', type: 'boolean' },
    { key: 'metalPlans', type: 'array', of: MetalPlan },
    { key: 'networkEdgeEnabled', type: 'boolean' },
    { key: 'networkEdgeCategories', type: 'array', of: NetworkEdgeCategory },
    { key: 'infraComponents', type: 'array', of: InfraComponent },
    { key: 'deploymentOffsets', type: 'array', of: DeploymentOffset },
    { key: 'capacity', type: Capacity, stringify: true },
  ];

  // Dijkstra's algorithm, adapted a little.
  // One important distinction is that we compute the path to multiple destination nodes at once.
  // The algorithm already computes most of it anyway, no sense in repeating it multiple times
  getPaths(to, metros) {
    // Set the initial distance of each to Infinity, except our starting metro
    const unvisited = metros.map((m) => ({ metro: m, distance: this.id.equals(m.id) ? 0 : Infinity }));
    const visited = [];

    // Pick our starting metro as the current one
    unvisited.sort((a, b) => a.distance - b.distance);
    let current = unvisited.shift();

    // Get destination metros (from "to") that have a direct connection with the starting metro
    const directTos = to
      .map((t) => current.metro.connectedMetros.find((connectedMetro) => connectedMetro.metro.id.equals(t.id)))
      .filter((directMetro) => directMetro !== undefined);
    const directs = directTos.map((direct) => {
      return { metro: direct.metro, latency: direct.latency, distance: direct.latency, prev: { distance: 0, metro: current.metro } };
    });

    // TODO: find an efficient way to stop the computation once all destination nodes have been visited,
    // because right now it will continue until ALL nodes have been visited.
    // Most obvious ways will loop through the array, which will actually worsen performance.

    while (unvisited.length) {
      // Compute the cost of each connected metro
      for (let neighbour of current.metro.connectedMetros) {
        const unvisitedNeighbour = unvisited.find((e) => e.metro.id.equals(neighbour.metro.id));
        // Use the ones in the original list
        if (unvisitedNeighbour) {
          const newDistance = current.distance + neighbour.latency;
          if (newDistance < unvisitedNeighbour.distance) {
            // Use the current distance if it's shorter
            unvisitedNeighbour.distance = current.distance + neighbour.latency;
            unvisitedNeighbour.prev = current;
            unvisitedNeighbour.latency = neighbour.latency;
          }
        }
      }

      // mark as visited
      visited.push(current);
      // Use the node with the shortest distance as the next one
      unvisited.sort((a, b) => a.distance - b.distance);
      current = unvisited.shift();
    }
    visited.push(current);

    const tos = to
      .map((t) =>
        // If destination metro have a direct connection with the starting metro return that simple path, if not return path from visited(path with less latency)
        !!directs.find((directMetros) => directMetros.metro.id.equals(t.id))
          ? directs.find((directMetros) => directMetros.metro.id.equals(t.id))
          : visited.find((v) => v.metro.id.equals(t.id))
      )
      // exclude nodes with no connection
      .filter((t) => t.distance !== Infinity);

    const paths = tos.map((t) => getPath(t));
    return { paths, tos };
  }
}

MetroConnection.fields = [
  { key: 'latency', type: 'number' },
  { key: 'metro', type: Metro },
];

export const MetroStatus = Object.freeze({
  OPEN: 'OPEN',
  UNDER_ACQUISITION: 'UNDER_ACQUISITION',
});
